import React, { useState, useEffect, useMemo, useRef } from 'react';
import { CHAINS, getLogicalChainKey, getChainDisplayNameForRequest } from '../config/chains';
import {
  APPLICATIONS,
  getRequestApplication,
  getApplicationDisplayName,
} from '../config/applications';
import {
  getAllowedChainKeys,
  getAllowedTokenSymbols,
  getAllowedApplications,
} from '../services/auditorService';
import SelectField from './SelectField';
import './AccessFilter.css';

const AccessFilter = ({
  onFilterChange,
  onClearFilters,
  auditorAccessControls,
  userAddress: controlledUserAddress,
  selectedChain: controlledChain = '',
  selectedToken: controlledToken = '',
  selectedApplication: controlledApplication = '',
  transactions = [],
  loading = false,
}) => {
  const [selectedChain, setSelectedChain] = useState(controlledChain || '');
  const [selectedToken, setSelectedToken] = useState(controlledToken || '');
  const [selectedApplication, setSelectedApplication] = useState(controlledApplication || '');
  const [seenChains, setSeenChains] = useState(() => new Map());
  const [seenTokens, setSeenTokens] = useState(() => new Map());
  const [seenApplications, setSeenApplications] = useState(() => new Map());
  const [chainCounts, setChainCounts] = useState(() => new Map());
  const [tokenCounts, setTokenCounts] = useState(() => new Map());
  const [applicationCounts, setApplicationCounts] = useState(() => new Map());
  const lastNotifiedRef = useRef('');

  const allowedChainKeys = useMemo(() => getAllowedChainKeys(auditorAccessControls), [auditorAccessControls]);
  const allowedTokenSymbols = useMemo(() => getAllowedTokenSymbols(auditorAccessControls), [auditorAccessControls]);
  const allowedApplications = useMemo(
    () => getAllowedApplications(auditorAccessControls),
    [auditorAccessControls]
  );

  useEffect(() => {
    setSelectedChain(controlledChain || '');
  }, [controlledChain]);

  useEffect(() => {
    setSelectedToken(controlledToken || '');
  }, [controlledToken]);

  useEffect(() => {
    setSelectedApplication(controlledApplication || '');
  }, [controlledApplication]);

  useEffect(() => {
    setSeenChains(new Map());
    setChainCounts(new Map());
  }, [controlledUserAddress]);

  useEffect(() => {
    setSeenTokens(new Map());
    setTokenCounts(new Map());
  }, [controlledUserAddress, selectedChain]);

  useEffect(() => {
    setSeenApplications(new Map());
    setApplicationCounts(new Map());
  }, [controlledUserAddress]);

  useEffect(() => {
    if (!transactions || transactions.length === 0) return;
    setSeenChains((prev) => {
      const next = new Map(prev);
      let changed = false;
      for (const tx of transactions) {
        const key = getLogicalChainKey(tx);
        if (key && !next.has(key)) {
          next.set(key, getChainDisplayNameForRequest(tx));
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    // Keep counts for chains not present in this (possibly filtered) batch,
    // so selecting one chain does not wipe the other option counts.
    setChainCounts((prev) => {
      const batch = new Map();
      for (const tx of transactions) {
        const key = getLogicalChainKey(tx);
        if (!key) continue;
        batch.set(key, (batch.get(key) || 0) + 1);
      }
      if (batch.size === 0) return prev;

      const next = new Map(prev);
      let changed = false;
      for (const [key, count] of batch) {
        if (next.get(key) !== count) {
          next.set(key, count);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [transactions]);

  useEffect(() => {
    if (!transactions || transactions.length === 0) return;
    setSeenTokens((prev) => {
      const next = new Map(prev);
      let changed = false;
      for (const tx of transactions) {
        const symbol = tx?.details?.token;
        if (!symbol) continue;
        const symbolKey = symbol.toUpperCase();
        if (!next.has(symbolKey)) {
          next.set(symbolKey, { symbol });
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    setTokenCounts((prev) => {
      const batch = new Map();
      for (const tx of transactions) {
        const symbol = tx?.details?.token;
        if (!symbol) continue;
        const key = symbol.toUpperCase();
        batch.set(key, (batch.get(key) || 0) + 1);
      }
      if (batch.size === 0) return prev;

      const next = new Map(prev);
      let changed = false;
      for (const [key, count] of batch) {
        if (next.get(key) !== count) {
          next.set(key, count);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [transactions]);

  useEffect(() => {
    if (!transactions || transactions.length === 0) return;
    setSeenApplications((prev) => {
      const next = new Map(prev);
      let changed = false;
      for (const tx of transactions) {
        const app = getRequestApplication(tx);
        const key = app.toLowerCase();
        if (!next.has(key)) {
          next.set(key, getApplicationDisplayName(app));
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    setApplicationCounts((prev) => {
      const batch = new Map();
      for (const tx of transactions) {
        const app = getRequestApplication(tx);
        const key = app.toLowerCase();
        batch.set(key, (batch.get(key) || 0) + 1);
      }
      if (batch.size === 0) return prev;

      const next = new Map(prev);
      let changed = false;
      for (const [key, count] of batch) {
        if (next.get(key) !== count) {
          next.set(key, count);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [transactions]);

  useEffect(() => {
    if (
      allowedChainKeys &&
      selectedChain &&
      CHAINS[selectedChain] &&
      !allowedChainKeys.includes(selectedChain)
    ) {
      setSelectedChain('');
    }
  }, [allowedChainKeys, selectedChain]);

  useEffect(() => {
    if (
      allowedTokenSymbols &&
      selectedToken &&
      !allowedTokenSymbols.some(
        (symbol) => symbol.toUpperCase() === (selectedToken || '').toUpperCase()
      )
    ) {
      setSelectedToken('');
    }
  }, [allowedTokenSymbols, selectedToken]);

  useEffect(() => {
    if (
      allowedApplications &&
      selectedApplication &&
      !allowedApplications.some(
        (app) => app.toLowerCase() === selectedApplication.toLowerCase()
      )
    ) {
      setSelectedApplication('');
    }
  }, [allowedApplications, selectedApplication]);

  useEffect(() => {
    const payload = {
      chain: selectedChain || null,
      token: selectedToken || null,
      application: selectedApplication || null,
      userAddress: (controlledUserAddress || '').trim() || null,
    };
    const signature = JSON.stringify(payload);
    if (lastNotifiedRef.current === signature) return;
    lastNotifiedRef.current = signature;
    onFilterChange(payload);
  }, [selectedChain, selectedToken, selectedApplication, controlledUserAddress, onFilterChange]);

  const handleClearFilters = () => {
    setSelectedChain('');
    setSelectedToken('');
    setSelectedApplication('');
    if (onClearFilters) {
      onClearFilters();
      return;
    }
    onFilterChange({
      chain: null,
      token: null,
      application: null,
      userAddress: (controlledUserAddress || '').trim() || null,
    });
  };

  const handleChainChange = (value) => {
    setSelectedChain(value);
    setSelectedToken('');
  };

  const chainOptions = useMemo(() => {
    const optionMap = new Map();

    if (allowedChainKeys && allowedChainKeys.length > 0) {
      for (const key of allowedChainKeys) {
        optionMap.set(key, CHAINS[key]?.name || key);
      }
    }

    for (const [value, label] of seenChains.entries()) {
      optionMap.set(value, label);
    }

    if (selectedChain && !optionMap.has(selectedChain)) {
      optionMap.set(selectedChain, CHAINS[selectedChain]?.name || `Chain ${selectedChain}`);
    }

    return Array.from(optionMap.entries())
      .map(([value, label]) => {
        const count = chainCounts.get(value);
        return {
          value,
          label: count != null ? `${label} · ${count}` : label,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allowedChainKeys, seenChains, selectedChain, chainCounts]);

  const tokenOptions = useMemo(() => {
    const optionMap = new Map();

    if (allowedTokenSymbols && allowedTokenSymbols.length > 0) {
      for (const symbol of allowedTokenSymbols) {
        // Prefer canonical casing from access controls / CHAINS (e.g. lbcoreUSDG).
        optionMap.set(symbol.toUpperCase(), symbol);
      }
    }

    for (const token of seenTokens.values()) {
      optionMap.set(token.symbol.toUpperCase(), token.symbol);
    }

    if (selectedToken && !optionMap.has(selectedToken.toUpperCase())) {
      optionMap.set(selectedToken.toUpperCase(), selectedToken);
    }

    return Array.from(optionMap.entries())
      .map(([key, symbol]) => {
        const count = tokenCounts.get(key);
        return {
          value: symbol,
          label: count != null ? `${symbol} · ${count}` : symbol,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allowedTokenSymbols, seenTokens, selectedToken, tokenCounts]);

  const applicationOptions = useMemo(() => {
    const optionMap = new Map();

    if (allowedApplications && allowedApplications.length > 0) {
      for (const app of allowedApplications) {
        optionMap.set(app.toLowerCase(), getApplicationDisplayName(app));
      }
    } else {
      for (const app of APPLICATIONS) {
        optionMap.set(app.id.toLowerCase(), app.name);
      }
    }

    for (const [key, label] of seenApplications.entries()) {
      optionMap.set(key, label);
    }

    if (selectedApplication) {
      const key = selectedApplication.toLowerCase();
      if (!optionMap.has(key)) {
        optionMap.set(key, getApplicationDisplayName(selectedApplication));
      }
    }

    return Array.from(optionMap.entries())
      .map(([key, label]) => {
        const count = applicationCounts.get(key);
        // Prefer canonical casing from APPLICATIONS / selected value
        const canonical =
          APPLICATIONS.find((a) => a.id.toLowerCase() === key)?.id ||
          (selectedApplication?.toLowerCase() === key ? selectedApplication : label);
        return {
          value: canonical,
          label: count != null ? `${label} · ${count}` : label,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allowedApplications, seenApplications, selectedApplication, applicationCounts]);

  const hasActiveFilters = selectedChain || selectedToken || selectedApplication;

  return (
    <div className="access-filter ui-panel">
      <div className="filter-header ui-panel-header">
        <h2>Access Filters</h2>
        {hasActiveFilters && (
          <button type="button" onClick={handleClearFilters} className="clear-filters-btn ui-btn ui-btn--ghost">
            Clear filters
          </button>
        )}
      </div>

      <div className="filter-grid" aria-busy={loading}>
        {loading ? (
          <>
            <div className="filter-group">
              <label>Chain</label>
              <span className="ui-skeleton filter-select-skeleton" />
            </div>
            <div className="filter-group">
              <label>Token</label>
              <span className="ui-skeleton filter-select-skeleton" />
            </div>
            <div className="filter-group">
              <label>Application</label>
              <span className="ui-skeleton filter-select-skeleton" />
            </div>
          </>
        ) : (
          <>
            <div className="filter-group">
              <label htmlFor="chain-select">Chain</label>
              <SelectField
                id="chain-select"
                value={selectedChain}
                onChange={handleChainChange}
                options={[
                  { value: '', label: 'All Chains', icon: null },
                  ...chainOptions,
                ]}
                placeholder="All Chains"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="token-select">Token</label>
              <SelectField
                id="token-select"
                value={selectedToken}
                onChange={setSelectedToken}
                options={[
                  { value: '', label: 'All Tokens', icon: null },
                  ...tokenOptions,
                ]}
                placeholder="All Tokens"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="application-select">Application</label>
              <SelectField
                id="application-select"
                value={selectedApplication}
                onChange={setSelectedApplication}
                options={[
                  { value: '', label: 'All Applications', icon: null },
                  ...applicationOptions,
                ]}
                placeholder="All Applications"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AccessFilter;
